import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Mail, Phone, MapPin, Shield, FileText } from 'lucide-react';

const CustomerTerms = () => {
  const lastUpdated = "January 12, 2026";

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Header */}
      <header className="bg-gradient-to-r from-blue-600 to-indigo-600 py-4 px-4 text-white shadow-lg sticky top-0 z-40">
        <div className="max-w-3xl mx-auto flex items-center gap-3">
          <Link to="/" className="p-1 hover:bg-white/20 rounded-lg transition-colors">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div>
            <h1 className="text-lg font-bold">Terms & Conditions</h1>
            <p className="text-blue-100 text-xs">Last updated: {lastUpdated}</p>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-3xl mx-auto px-4 py-8">
        <div className="bg-white rounded-2xl shadow-lg border border-slate-100 p-6 sm:p-8 space-y-8">
          
          {/* Introduction */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                <FileText className="w-5 h-5 text-blue-600" />
              </div>
              <h2 className="text-xl font-bold text-slate-800">1. About Us (Legal Entity)</h2>
            </div>
            <p className="text-slate-600 leading-relaxed">
              These Terms & Conditions ("Terms") govern your access to and use of the Nanokart.ai website, apps, and related services (collectively, the "Platform"). The Platform is operated by <strong>Nanokart India Private Limited</strong> ("Company", "Nanokart", "we", "us", "our").
            </p>
            <div className="mt-4 bg-slate-50 rounded-xl p-4 space-y-2">
              <div className="flex items-start gap-2 text-sm">
                <MapPin className="w-4 h-4 text-slate-500 mt-0.5 flex-shrink-0" />
                <span className="text-slate-600">Registered Office: Vaswani Exquisite, ITPL Main Road, Near Hotel Zuri, Whitefield, Bengaluru, Karnataka - 560048, India</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Mail className="w-4 h-4 text-slate-500" />
                <a href="mailto:team@nanokart.ai" className="text-blue-600 hover:underline">team@nanokart.ai</a>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Phone className="w-4 h-4 text-slate-500" />
                <a href="tel:+919337723626" className="text-blue-600 hover:underline">+91-93377-23626</a>
              </div>
            </div>
          </section>

          {/* Section 2 */}
          <section>
            <h2 className="text-xl font-bold text-slate-800 mb-3">2. Acceptance of Terms</h2>
            <p className="text-slate-600 leading-relaxed">
              By accessing, browsing, registering, or using the Platform, you agree to these Terms, our Privacy Policy, and other policies published on the Platform. If you disagree, please do not use the Platform.
            </p>
          </section>

          {/* Section 3 */}
          <section>
            <h2 className="text-xl font-bold text-slate-800 mb-3">3. Eligibility</h2>
            <p className="text-slate-600 leading-relaxed mb-3">You represent that you are:</p>
            <ul className="list-disc list-inside text-slate-600 space-y-1 ml-2">
              <li>At least 18 years old, or using the Platform under the supervision of a parent/guardian; and</li>
              <li>Legally capable of entering into a binding contract under the Indian Contract Act, 1872.</li>
            </ul>
          </section>

          {/* Section 4 */}
          <section>
            <h2 className="text-xl font-bold text-slate-800 mb-3">4. Account Registration & Responsibility</h2>
            <p className="text-slate-600 leading-relaxed mb-3">If you create an account or register for our services, you agree to:</p>
            <ul className="list-disc list-inside text-slate-600 space-y-1 ml-2">
              <li>Provide accurate, current information;</li>
              <li>Maintain confidentiality of login credentials;</li>
              <li>Promptly notify us of suspected unauthorized access.</li>
            </ul>
            <p className="text-slate-600 mt-3">You are responsible for all activity conducted through your account.</p>
          </section>

          {/* Section 5 */}
          <section>
            <h2 className="text-xl font-bold text-slate-800 mb-3">5. Platform Use Rules (Prohibited Conduct)</h2>
            <p className="text-slate-600 leading-relaxed mb-3">You must not:</p>
            <ul className="list-disc list-inside text-slate-600 space-y-1 ml-2">
              <li>Violate any law, regulation, or third-party rights;</li>
              <li>Upload content that is unlawful, abusive, obscene, infringing, or misleading;</li>
              <li>Attempt to hack, scrape, reverse engineer, disrupt, or overload the Platform;</li>
              <li>Use bots, automated scripts, or unauthorized tools to access the Platform;</li>
              <li>Impersonate another person or misrepresent identity.</li>
            </ul>
            <p className="text-slate-600 mt-3">We may suspend or terminate access for violations.</p>
          </section>

          {/* Section 6 - TryOn Studio */}
          <section className="bg-blue-50 rounded-xl p-5 border border-blue-100">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center">
                <Shield className="w-5 h-5 text-white" />
              </div>
              <h2 className="text-xl font-bold text-slate-800">6. Virtual Try-On Studio - Critical Terms</h2>
            </div>
            <p className="text-slate-600 leading-relaxed mb-4">
              The Virtual Try-On Studio allows you to upload a photo and generate virtual try-on images (including themed lifestyle scenes).
            </p>

            <h3 className="font-semibold text-slate-800 mt-4 mb-2">6.1 Your Ownership; Our Limited Processing Right</h3>
            <ul className="list-disc list-inside text-slate-600 space-y-1 ml-2">
              <li>You retain ownership of your uploaded photo.</li>
              <li>You grant us a limited, non-exclusive, revocable right to process your uploaded photo solely to provide Try-On Studio outputs to you.</li>
              <li>We do not claim ownership of your photo.</li>
            </ul>

            <h3 className="font-semibold text-slate-800 mt-4 mb-2">6.2 What You May Upload</h3>
            <p className="text-slate-600 mb-2">You agree that:</p>
            <ul className="list-disc list-inside text-slate-600 space-y-1 ml-2">
              <li>You will upload only your own photo (or you have explicit consent and legal rights);</li>
              <li>You will not upload photos of minors;</li>
              <li>You will not upload third-party images without permission;</li>
              <li>You will not upload illegal/objectionable content.</li>
            </ul>

            <h3 className="font-semibold text-slate-800 mt-4 mb-2">6.3 Output Disclaimer</h3>
            <p className="text-slate-600">
              Try-On Studio outputs are simulations for visualization. Actual fit, drape, and color may differ based on fabric, tailoring, lighting, and body posture.
            </p>

            <h3 className="font-semibold text-slate-800 mt-4 mb-2">6.4 Service Availability</h3>
            <p className="text-slate-600">
              Try-On Studio may be unavailable due to maintenance, high load, or vendor limitations. We may change or discontinue the feature.
            </p>

            <h3 className="font-semibold text-slate-800 mt-4 mb-2">6.5 AI-Generated Output Disclaimer</h3>
            <p className="text-slate-600 mb-2">
              Try-On Studio generates images using artificial intelligence-based systems. These outputs are visual simulations only.
            </p>
            <ul className="list-disc list-inside text-slate-600 space-y-1 ml-2 text-sm">
              <li>AI-generated images may differ from real-world appearance, actual garment fit, fabric behavior, body proportions, skin tone, posture, facial or body features.</li>
              <li>Outputs may contain visual inaccuracies, distortions, artifacts, or unrealistic elements.</li>
              <li>Results are not guaranteed to be accurate, flattering, or representative of how a product or person appears in real life.</li>
            </ul>
            <p className="text-slate-600 mt-3 text-sm">
              By using Try-On Studio, you acknowledge and agree that the feature is provided "as-is" and "as-available", and dissatisfaction with AI-generated outputs does not give rise to any legal claim.
            </p>

            <h3 className="font-semibold text-slate-800 mt-4 mb-2">6.6 User Responsibility for Uploaded Content</h3>
            <p className="text-slate-600 mb-2">You are solely responsible for the content you upload. You agree that you will not upload:</p>
            <ul className="list-disc list-inside text-slate-600 space-y-1 ml-2 text-sm">
              <li>Pornographic, sexually explicit, or obscene content;</li>
              <li>Content involving minors;</li>
              <li>Hate speech, extremist, abusive, defamatory, or harassing content;</li>
              <li>Content that violates privacy, intellectual property, or publicity rights;</li>
              <li>Any content that is illegal under Indian law.</li>
            </ul>
          </section>

          {/* Section 7 */}
          <section>
            <h2 className="text-xl font-bold text-slate-800 mb-3">7. User Content (Reviews, Photos, Comments)</h2>
            <p className="text-slate-600 leading-relaxed">
              If you submit reviews, ratings, comments, or other content, you confirm it is truthful and lawful. You grant us a limited license to display such content on the Platform for platform operations and customer information.
            </p>
          </section>

          {/* Section 8 */}
          <section>
            <h2 className="text-xl font-bold text-slate-800 mb-3">8. Intellectual Property</h2>
            <p className="text-slate-600 leading-relaxed">
              All trademarks, logos, UI, product photography (unless stated otherwise), text, designs, and software on the Platform are owned by or licensed to Nanokart India Private Limited and protected by applicable laws. You may not copy, reproduce, distribute, or create derivative works without written permission.
            </p>
          </section>

          {/* Section 9 */}
          <section>
            <h2 className="text-xl font-bold text-slate-800 mb-3">9. Third-Party Links</h2>
            <p className="text-slate-600 leading-relaxed">
              The Platform may contain third-party links. We are not responsible for third-party content, policies, or practices.
            </p>
          </section>

          {/* Section 10 */}
          <section>
            <h2 className="text-xl font-bold text-slate-800 mb-3">10. Limitation of Liability</h2>
            <p className="text-slate-600 leading-relaxed">
              To the fullest extent permitted by Indian law, Nanokart India Private Limited is not liable for indirect, incidental, special, or consequential damages arising from use of the Platform, including delays, interruptions, or Try-On Studio outputs.
            </p>
          </section>

          {/* Section 11 */}
          <section>
            <h2 className="text-xl font-bold text-slate-800 mb-3">11. Indemnity</h2>
            <p className="text-slate-600 leading-relaxed">
              You agree to indemnify and hold harmless Nanokart India Private Limited against claims arising from your misuse of the Platform, violation of law, or infringement of third-party rights.
            </p>
          </section>

          {/* Section 12 */}
          <section>
            <h2 className="text-xl font-bold text-slate-800 mb-3">12. Termination</h2>
            <p className="text-slate-600 leading-relaxed">
              We may suspend or terminate access to the Platform if we reasonably believe you have violated these Terms or applicable law.
            </p>
          </section>

          {/* Section 13 */}
          <section>
            <h2 className="text-xl font-bold text-slate-800 mb-3">13. Changes to Terms</h2>
            <p className="text-slate-600 leading-relaxed">
              We may update these Terms from time to time by posting an updated version with a revised "Last updated" date. Continued use indicates acceptance.
            </p>
          </section>

          {/* Section 14 */}
          <section>
            <h2 className="text-xl font-bold text-slate-800 mb-3">14. Governing Law & Jurisdiction</h2>
            <p className="text-slate-600 leading-relaxed">
              These Terms are governed by the laws of India. Courts in Bengaluru, Karnataka, shall have exclusive jurisdiction.
            </p>
          </section>

          {/* Section 15 - Contact */}
          <section className="bg-slate-50 rounded-xl p-5">
            <h2 className="text-xl font-bold text-slate-800 mb-3">15. Contact Us</h2>
            <p className="text-slate-600 mb-4">For questions or concerns regarding these Terms, please contact us:</p>
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm">
                <Mail className="w-4 h-4 text-blue-600" />
                <a href="mailto:team@nanokart.ai" className="text-blue-600 hover:underline font-medium">team@nanokart.ai</a>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Phone className="w-4 h-4 text-blue-600" />
                <a href="tel:+919337723626" className="text-blue-600 hover:underline font-medium">+91-93377-23626</a>
              </div>
            </div>
          </section>

        </div>

        {/* Footer */}
        <div className="text-center mt-8 text-sm text-slate-500">
          <p>© {new Date().getFullYear()} Nanokart India Private Limited. All rights reserved.</p>
        </div>
      </main>
    </div>
  );
};

export default CustomerTerms;
